import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { getTicks, scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, useTooltip } from '@visx/tooltip';
import React from 'react';

import {
  BarChartData,
  formatAxisLabel,
  formatTooltipLabel,
  getIntervalUnit,
  getMaxTickWidth,
  getUpperBound,
  numberFormatter,
} from './helpers';

type Props = {
  chartData: BarChartData;
  tooltipTitle?: string;
};

export const BarChart = ({ chartData, tooltipTitle }: Props) => {
  const unit = getIntervalUnit(chartData);

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<{ name: string; value: number }>();

  return (
    <ParentSize>
      {(parent: { height: number; width: number }) => {
        // Y axis config
        const maxYTicks = 4;
        const paddingBottom = 35;
        const yMax = parent.height && parent.height - paddingBottom;
        const maxValue = Math.max(...chartData.map(data => data[1]));

        const upperYBound = getUpperBound(maxValue, maxYTicks) || 1;
        const numYTicks = Math.min(upperYBound, maxYTicks);
        const yScale = scaleLinear({
          range: [yMax, 0],
          domain: [0, upperYBound],
        });
        const yTicks = getTicks(yScale, numYTicks);

        // X axis config
        const paddingLeft = getMaxTickWidth(yTicks);
        const xMax = parent.width - paddingLeft;

        const xScale = scaleBand({
          range: [0, xMax],
          domain: chartData.map(data => data[0]),
          padding: 0.4,
        });
        const numXTicks = 6;
        const xTicks = getTicks(xScale, numXTicks);

        return (
          <div data-testid="bar-chart" className="relative h-full w-full">
            <svg className="h-full w-full overflow-visible">
              <Group>
                {chartData.map((data, index) => {
                  const xVal = data[0];
                  const yVal = data[1];
                  const barWidth = xScale.bandwidth();
                  const barHeight = yMax && yMax - (yScale(data[1]) ?? 0);
                  const barX = (xScale(xVal) || 0) + paddingLeft;
                  const barY = yMax - barHeight;

                  return (
                    <Bar
                      key={index}
                      x={barX}
                      y={barY}
                      width={barWidth}
                      height={barHeight}
                      className="fill-blue-500 opacity-40"
                      onMouseOver={e =>
                        showTooltip({
                          tooltipData: { name: xVal, value: yVal },
                          tooltipTop: localPoint(e)?.y,
                          tooltipLeft: localPoint(e)?.x,
                        })
                      }
                      onMouseLeave={hideTooltip}
                    />
                  );
                })}
              </Group>
              <AxisBottom
                scale={xScale}
                top={yMax}
                left={paddingLeft}
                numTicks={numXTicks}
                tickValues={xTicks}
                hideTicks
                strokeWidth={2}
                axisLineClassName="stroke-gray-300"
                tickLabelProps={tick => ({
                  textAnchor: tick === xTicks[0] ? 'start' : 'middle',
                })}
                tickComponent={props => {
                  const formattedAxisLabel = formatAxisLabel(
                    xTicks,
                    props.formattedValue,
                    unit,
                  );

                  return (
                    <svg textAnchor={props.textAnchor}>
                      <text
                        x={props.x}
                        y={props.y}
                        className="font-inter fill-gray-650 text-xs"
                      >
                        <tspan>{formattedAxisLabel.text}</tspan>
                        <tspan x={props.x} dy="1.2em">
                          {formattedAxisLabel.subText}
                        </tspan>
                      </text>
                    </svg>
                  );
                }}
              />
              <AxisLeft
                scale={yScale}
                left={paddingLeft}
                numTicks={numYTicks}
                axisClassName="fill-gray-650"
                axisLineClassName="stroke-gray-300"
                tickLength={4}
                hideTicks
                strokeWidth={2}
                tickFormat={numberFormatter}
                tickLabelProps={() => ({
                  fontSize: 12,
                  textAnchor: 'end',
                  dy: '0.33em',
                  dx: '-.33em',
                })}
                labelClassName="font-inter fill-gray-650"
                labelOffset={10}
              />
            </svg>
            {tooltipOpen && tooltipData && (
              <TooltipWithBounds
                key={Math.random()} // Set to random so it correctly updates with parent bounds
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  backgroundColor: '#05141f',
                  opacity: 0.7,
                  padding: 16,
                  borderRadius: 8,
                }}
              >
                <div className="flex justify-between gap-4">
                  <div>
                    <p className="font-inter mb-6 text-sm text-white">
                      {formatTooltipLabel(tooltipData.name, unit)}
                    </p>
                    <p className="font-inter text-sm text-white">
                      {tooltipTitle}
                    </p>
                  </div>
                  <div className="font-inter self-end text-sm font-bold text-white">
                    <p>{tooltipData.value}</p>
                  </div>
                </div>
              </TooltipWithBounds>
            )}
          </div>
        );
      }}
    </ParentSize>
  );
};
